// src/modules/myvault/views/AutomationRuleBuilder.tsx

// What? Create / edit automation rule. Single scrollable form with Basics,
//       Trigger, Conditions, Actions sections + sticky action bar.
// Why?  R-3's primary authoring surface. Pairs with AutomationRulesList as
//       the place admins land after clicking "new" or an existing rule.
// How?  Local state for the in-flight rule; save calls create or update via
//       the automation hooks; the Test button enables only once the rule
//       has been saved (backend requires a persisted rule to dry-run).

import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useParams } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useAutomationRule,
  useAutomationRuleMutations,
  type AutomationCondition,
  type AutomationRuleAction,
  type AutomationRuleCreate,
  type AutomationRuleUpdate,
  type TriggerType,
} from "../hooks/useAutomationRules";
import { useSchemaColumns, useSchemas } from "../hooks/useSchemas";
import ConditionBuilder from "../components/automations/ConditionBuilder";
import ActionListEditor from "../components/automations/ActionListEditor";
import TestFireDialog from "../components/automations/TestFireDialog";

const TRIGGER_OPTIONS: {
  value: TriggerType;
  label: string;
  description: string;
  enabled: boolean;
}[] = [
  {
    value: "ROW_CREATED",
    label: "Row created",
    description: "Fires when a new row is added to the target table.",
    enabled: true,
  },
  {
    value: "FIELD_CHANGED",
    label: "Field changed",
    description: "Fires when the named field changes on a row.",
    enabled: true,
  },
  {
    value: "FIELD_MATCHES",
    label: "Field matches",
    description: "Fires when a field changes to match a specific value.",
    enabled: true,
  },
  {
    value: "BUTTON_CLICK",
    label: "Button click (named rule)",
    description:
      "Fires when a canvas Action Button references this rule by name. Rare — prefer configuring action buttons inline on the canvas.",
    enabled: true,
  },
  {
    value: "SCHEDULED",
    label: "Scheduled (coming soon)",
    description:
      "Fires on a cron schedule. Backend scheduler wiring for rule-triggered fires is not yet available.",
    enabled: false,
  },
];

function validateRule(
  name: string,
  tableId: string,
  triggerType: TriggerType,
  triggerConfig: Record<string, unknown>,
  actions: AutomationRuleAction[],
): string | null {
  if (!name.trim()) return "Name is required.";
  if (!tableId) return "Pick a target table.";
  if (triggerType === "FIELD_CHANGED" || triggerType === "FIELD_MATCHES") {
    if (!triggerConfig.field) {
      return "Select a field for this trigger.";
    }
  }
  if (triggerType === "FIELD_MATCHES" && triggerConfig.value === undefined) {
    return "FIELD_MATCHES requires a value.";
  }
  for (const [i, action] of actions.entries()) {
    if (action.type === "SEND_EMAIL") {
      const cfg = action.config || {};
      if (!cfg.template) return `Action ${i + 1}: pick an email template.`;
      const recipients = Array.isArray(cfg.recipients) ? cfg.recipients : [];
      if (recipients.length === 0) {
        return `Action ${i + 1}: add at least one recipient.`;
      }
    }
    if (action.type === "CREATE_RECORD" && !action.config?.view_id) {
      return `Action ${i + 1}: pick a view for CREATE_RECORD.`;
    }
    if (action.type === "UPDATE_FIELD") {
      if (!action.config?.view_id || !action.config?.field) {
        return `Action ${i + 1}: UPDATE_FIELD needs a view and a field.`;
      }
    }
    if (action.type === "RUN_SKILL" && !action.config?.skill_id) {
      return `Action ${i + 1}: pick a skill for RUN_SKILL.`;
    }
    if (action.type === "WEBHOOK" && !action.config?.url) {
      return `Action ${i + 1}: webhook URL required.`;
    }
    if (action.type === "INSTANTIATE_TEMPLATE" && !action.config?.template_id) {
      return `Action ${i + 1}: template id required.`;
    }
  }
  return null;
}

export default function AutomationRuleBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const isEditing = Boolean(id && id !== "new");
  const ruleId = isEditing ? (id as string) : null;

  const { schemas } = useSchemas(workspaceId);
  const {
    rule,
    loading: ruleLoading,
    error: ruleError,
  } = useAutomationRule(workspaceId, ruleId);
  const { createRule, updateRule, testRule } =
    useAutomationRuleMutations(workspaceId);

  const [name, setName] = useState("");
  const [tableId, setTableId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [allowCascading, setAllowCascading] = useState(false);
  const [triggerType, setTriggerType] = useState<TriggerType>("ROW_CREATED");
  const [triggerConfig, setTriggerConfig] = useState<Record<string, unknown>>(
    {},
  );
  const [conditions, setConditions] = useState<AutomationCondition | null>(
    null,
  );
  const [actions, setActions] = useState<AutomationRuleAction[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [testOpen, setTestOpen] = useState(false);

  const { columns } = useSchemaColumns(workspaceId, tableId || null);

  // Hydrate form when an existing rule loads.
  useEffect(() => {
    if (!isEditing) return;
    if (rule) {
      setName(rule.name);
      setTableId(rule.table_id);
      setEnabled(rule.enabled);
      setAllowCascading(rule.allow_cascading);
      setTriggerType(rule.trigger_type);
      setTriggerConfig(rule.trigger_config || {});
      setConditions(rule.conditions);
      setActions(rule.actions || []);
    }
  }, [isEditing, rule]);

  const actionContext = useMemo(
    () => ({
      workspaceId,
      tableId: tableId || null,
      columns,
    }),
    [workspaceId, tableId, columns],
  );

  const handleSave = async (): Promise<string | null> => {
    setError(null);
    const validationError = validateRule(
      name,
      tableId,
      triggerType,
      triggerConfig,
      actions,
    );
    if (validationError) {
      setError(validationError);
      return null;
    }

    setSaving(true);
    try {
      const payload: AutomationRuleCreate = {
        name: name.trim(),
        table_id: tableId,
        enabled,
        allow_cascading: allowCascading,
        trigger_type: triggerType,
        trigger_config: triggerConfig,
        conditions,
        actions,
      };

      if (isEditing && ruleId) {
        const updateBody: AutomationRuleUpdate = payload;
        await updateRule(ruleId, updateBody);
        setFeedback("Rule saved");
        setTimeout(() => setFeedback(null), 2500);
        return ruleId;
      }
      const created = await createRule(payload);
      setFeedback("Rule created");
      // Navigate to the edit URL so subsequent saves hit PUT and the Test
      // button can do a dry-run against the persisted rule.
      navigate(`../${created.id}`, { replace: true });
      return created.id;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save rule");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (rowId: string) => {
    const current = ruleId ?? (await handleSave());
    if (!current) throw new Error("Save the rule before testing.");
    return testRule(current, rowId);
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }
  if (isEditing && ruleLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isEditing && ruleError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{ruleError}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto", pb: 12 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="plain"
            color="neutral"
            startDecorator={<ArrowBackIcon />}
            onClick={() => navigate("..")}
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography level="h3">
              {isEditing ? "Edit automation rule" : "New automation rule"}
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              {isEditing
                ? "Changes are saved when you click Save."
                : "Event-triggered automation for the MyVault engine."}
            </Typography>
          </Box>
        </Stack>

        {error && <Alert color="danger">{error}</Alert>}
        {feedback && <Alert color="success">{feedback}</Alert>}

        <AccordionGroup variant="outlined">
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography level="title-sm">Basics</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    size="sm"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Notify HR on salary change"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Target table</FormLabel>
                  <Select
                    size="sm"
                    value={tableId}
                    onChange={(_, val) => setTableId((val as string) || "")}
                    placeholder="Select a table"
                  >
                    <Option value="">None</Option>
                    {schemas.map((t) => (
                      <Option key={t.id} value={t.id}>
                        {t.display_name || t.table_name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={3} alignItems="center">
                  <FormControl orientation="horizontal">
                    <Switch
                      size="sm"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                    />
                    <FormLabel sx={{ m: 0, ml: 1 }}>Enabled</FormLabel>
                  </FormControl>
                  <Tooltip
                    title="Rules triggering rules can create loops. Only enable if you need chained automation."
                    variant="soft"
                  >
                    <FormControl orientation="horizontal">
                      <Switch
                        size="sm"
                        checked={allowCascading}
                        onChange={(e) => setAllowCascading(e.target.checked)}
                      />
                      <FormLabel sx={{ m: 0, ml: 1 }}>
                        Allow cascading
                      </FormLabel>
                    </FormControl>
                  </Tooltip>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography level="title-sm">Trigger</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Trigger type</FormLabel>
                  <Select
                    size="sm"
                    value={triggerType}
                    onChange={(_, val) => {
                      const next = (val as TriggerType) || "ROW_CREATED";
                      setTriggerType(next);
                      setTriggerConfig({});
                    }}
                  >
                    {TRIGGER_OPTIONS.map((opt) => (
                      <Option
                        key={opt.value}
                        value={opt.value}
                        disabled={!opt.enabled}
                      >
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    {
                      TRIGGER_OPTIONS.find((o) => o.value === triggerType)
                        ?.description
                    }
                  </FormHelperText>
                </FormControl>

                {(triggerType === "FIELD_CHANGED" ||
                  triggerType === "FIELD_MATCHES") && (
                  <FormControl>
                    <FormLabel>Field</FormLabel>
                    <Select
                      size="sm"
                      value={
                        typeof triggerConfig.field === "string"
                          ? triggerConfig.field
                          : ""
                      }
                      onChange={(_, val) =>
                        setTriggerConfig((prev) => ({
                          ...prev,
                          field: (val as string) || "",
                        }))
                      }
                      placeholder={
                        columns.length === 0
                          ? "Pick a table first"
                          : "Select field"
                      }
                      disabled={columns.length === 0}
                    >
                      {columns.map((c) => (
                        <Option key={c.id} value={c.column_name}>
                          {c.display_name || c.column_name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {triggerType === "FIELD_MATCHES" && (
                  <FormControl>
                    <FormLabel>Value to match</FormLabel>
                    <Input
                      size="sm"
                      value={
                        triggerConfig.value === undefined ||
                        triggerConfig.value === null
                          ? ""
                          : String(triggerConfig.value)
                      }
                      onChange={(e) =>
                        setTriggerConfig((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                )}

                {triggerType === "SCHEDULED" && (
                  <Alert color="warning" variant="soft">
                    Scheduled triggers are not wired to a rule-scheduler yet.
                    Save a rule with this trigger, but it will not fire until
                    the backend scheduler lands.
                  </Alert>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary>
              <Typography level="title-sm">Conditions (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ConditionBuilder
                value={conditions}
                onChange={setConditions}
                columns={columns}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography level="title-sm">Actions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ActionListEditor
                actions={actions}
                onChange={setActions}
                context={actionContext}
              />
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>

        <Card variant="outlined">
          <CardContent>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Test-fire runs the rule against a real row without performing side
              effects. SEND_EMAIL force-redirects to the rule creator once the
              R-6d test-mode backend lands.
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "background.body",
          borderTop: "1px solid",
          borderColor: "divider",
          p: 1.5,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          zIndex: 10,
        }}
      >
        <Button
          variant="plain"
          color="neutral"
          onClick={() => navigate("..")}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<PlayArrowIcon />}
          onClick={() => setTestOpen(true)}
          disabled={saving}
        >
          {isEditing ? "Test" : "Save & test"}
        </Button>
        <Button
          startDecorator={<SaveIcon />}
          onClick={() => handleSave()}
          loading={saving}
        >
          Save
        </Button>
      </Box>

      <TestFireDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        onRun={runTest}
      />
    </Box>
  );
}
